/**
 * Module summary:
 * This component provides an embedded MFA enrollment workflow for profile
 * pages. It handles phone capture, SMS-code verification, and optional phone
 * replacement when the user already has MFA enabled.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import {
  initRecaptcha,
  isMfaEnrolled,
  startMfaEnrollment,
  completeMfaEnrollment,
} from '../../services/auth/mfaService';

interface MfaEnrollmentProps {
  userPhone?: string;
  onPhoneVerified?: (phoneE164: string) => Promise<void> | void;
}

type Step = 'idle' | 'phone' | 'verify';

export const MfaEnrollment: React.FC<MfaEnrollmentProps> = ({ userPhone, onPhoneVerified }) => {
  const [enrolled, setEnrolled] = useState(isMfaEnrolled());
  const [step, setStep] = useState<Step>('idle');
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [phone, setPhone] = useState(() => {
    const p = userPhone || '';
    return p.startsWith('+1') ? p.slice(2) : p;
  });
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (recaptchaRef.current) {
      // Initialize reCAPTCHA whenever the widget container is mounted so Firebase Phone Auth can send SMS challenges.
      initRecaptcha('mfa-recaptcha-container');
    }
  }, [step]);

  const handleStartEnrollment = useCallback(async () => {
    const digits = phone.trim().replace(/\D/g, '');
    if (digits.length !== 10) {
      // Require a 10-digit US number because the current enrollment flow formats phone numbers as +1XXXXXXXXXX.
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      // Prefix with +1 so the backend/Firebase service receives a valid E.164 phone number.
      const vid = await startMfaEnrollment(`+1${digits}`);
      setVerificationId(vid);
      setStep('verify');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  const handleVerify = useCallback(async () => {
    if (!code.trim()) {
      // Stop early when the code field is blank so the user can correct input before a network call.
      setError('Please enter the verification code.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const digits = phone.trim().replace(/\D/g, '');
      const phoneE164 = `+1${digits}`;
      // Pass replaceExisting when updating a phone so MFA moves from the old number to the newly verified number.
      await completeMfaEnrollment(verificationId, code.trim(), {
        replaceExisting: updatingPhone,
        phoneNumber: phoneE164,
      });
      if (onPhoneVerified) {
        // Let the parent profile page persist the verified phone number after enrollment succeeds.
        await onPhoneVerified(phoneE164);
      }
      setEnrolled(true);
      setStep('idle');
      setSuccess(updatingPhone ? 'Phone number updated and re-verified for MFA.' : 'MFA is enabled and verified.');
      setUpdatingPhone(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  }, [code, verificationId, updatingPhone, phone, onPhoneVerified]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-10 w-full flex flex-col items-center">
      <h3 className="text-lg font-bold text-slate-900 mb-2">SMS Multi-Factor Authentication</h3>
      <p className="text-sm text-slate-500 mb-6 text-center max-w-md">
        MFA is required for all accounts. Your phone number is used to verify your identity during sign-in.
      </p>

      <div id="mfa-recaptcha-container" ref={recaptchaRef} />

      {error && (
        <div className="w-full max-w-md rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-4 text-center">
          {error}
        </div>
      )}
      {success && (
        <div className="w-full max-w-md rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 mb-4 text-center">
          {success}
        </div>
      )}

      {enrolled && step === 'idle' ? (
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold px-4 py-2 mb-4">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M8 12.5l2.5 2.5L16 9.5" />
            </svg>
            MFA Enabled
          </span>
          <p className="text-sm text-slate-500 mb-4">
            SMS verification is active on your account.
          </p>
          <Button size="sm" onClick={() => { setUpdatingPhone(true); setStep('phone'); setError(''); setSuccess(''); }} loading={loading}>
            Update Phone Number
          </Button>
        </div>
      ) : step === 'idle' ? (
        <Button size="sm" onClick={() => { setUpdatingPhone(false); setStep('phone'); setError(''); setSuccess(''); }} loading={loading}>
          Enable MFA
        </Button>
      ) : step === 'phone' ? (
        <div className="w-full max-w-md space-y-4">
          <Input
            label="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            fullWidth
          />
          <p className="text-xs text-slate-400">
            Enter your 10-digit phone number.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" size="sm" onClick={() => { setStep('idle'); setError(''); setSuccess(''); setUpdatingPhone(false); }}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleStartEnrollment} loading={loading}>
              Send Code
            </Button>
          </div>
        </div>
      ) : step === 'verify' ? (
        <div className="w-full max-w-md space-y-4">
          <p className="text-sm text-slate-600 text-center">
            A verification code has been sent to <strong>{phone}</strong>.
          </p>
          <Input
            label="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            fullWidth
          />
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" size="sm" onClick={() => { setStep('phone'); setError(''); setSuccess(''); }}>
              Back
            </Button>
            <Button size="sm" onClick={handleVerify} loading={loading}>
              {updatingPhone ? 'Verify &amp; Update' : 'Verify &amp; Enable'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MfaEnrollment;
